
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import theme from "../../../assets/theme";
import { PrimaryCTAButton } from "../../common/Buttons";
import ChatModal from "./ChatModal";
import COLORS from "../../../assets/Colors";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import "./Timeline.css";
import EventService from "../../../services/EventService";
import useViewer from "../../../hooks/useViewer";
import LiveTickers from "../../schedules/Liveresources/Tickers";
import Feedback from "../../schedules/Liveresources/Feedback";
import VideoBox from "../../common/VideoBox";
import MenuBurger from "../../common/ActionBar/MenuBurger";
import { navigate, useLocation } from "@reach/router";
import AgendaTab from "../../schedules/MenuBar/Agenda";
import SpeakersTab from "../../schedules/MenuBar/Speakers";
import ResourcesTab from "../../schedules/MenuBar/Resources";
import VideosTab from "../../schedules/MenuBar/Videos";
import HostService from "../../../services/HostService";
import useLoader from "../../../hooks/useLoader";
import PollBarChart from "../../schedules/MenuBar/PollBarChart";
import GeneralGuidelinesTab from "../../schedules/MenuBar/GeneralGuidelines";
import { DrawerContainer } from "../Register";
import VideoPlayer from "../../common/Video";
import FAQs from "../../schedules/MenuBar/Faqs";
import GuidelinesDialog from "../../schedules/LiveEvent/GuidelinesDialog";
import { Header } from "../Header/Header";
import { Layout } from "../GalleryViewRoute";
import { HeroContainer } from "../Register";
import GetAppIcon from "@material-ui/icons/GetApp";
const POLL_INTERVAL_MS = 2000;

const AgmLive = () => {
  const buttonRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();
  const { setGlobalState } = useViewer();
  const location = useLocation();
  const { setLoader } = useLoader();

  const [userData, setUserData] = useState();
  const [modal, setModal] = useState({ state: false });
  const [fields, setFields] = useState({});
  const [initialState, setInitialState] = useState();
  const [pollResult, setPollResult] = useState([]);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  /*
   * ============================================================
   * AGM EVENT ID ORCHESTRATION
   * ============================================================
   */

  const params = new URLSearchParams(window.location.search);

  const eventIdFromUrl = params.get("eventId");

  const eventId =
    eventIdFromUrl || localStorage.getItem("eventId");

  /*
   * ============================================================
   * FETCH EVENT INFORMATION
   * ============================================================
   */

  const fetchEventInfo = async ({
    showLoader = false,
  } = {}) => {
    if (!eventId) {
      console.log("NO EVENT ID");
      return;
    }

    console.log(
      "FETCH EVENT INFO START:",
      eventId,
      new Date().toLocaleTimeString(),
    );

    try {
      if (showLoader) {
        setLoader({
          state: true,
          message: "Fetching AGM event...",
        });
      }

      const response = await EventService.getEvent(
        eventId,
        "live",
      );

      console.log(
        "FETCH EVENT INFO RESPONSE:",
        new Date().toLocaleTimeString(),
        response,
      );

      setUserData(response);

      setGlobalState(response);

      /*
       * FEEDBACK
       */

      setFields(
        response?.feedback?.feedbackForm || {},
      );

      if (
        response?.feedback?.feedbackForm &&
        Object.keys(
          response.feedback.feedbackForm,
        ).length !== 0
      ) {
        setInitialState(
          Object.keys(
            response.feedback.feedbackForm,
          ).reduce((acc, key) => {
            const firstFieldType =
              response.feedback.feedbackForm[key]?.[0]
                ?.type;

            acc[key] =
              firstFieldType === "checkbox" ? [] : "";

            return acc;
          }, {}),
        );
      }

      /*
       * POLLING RESULTS
       */

      if (
        response?.polling?.pollQuestions?.some(
          (poll) => poll?.pollResult,
        )
      ) {
        const pollResponse =
          await HostService.getPollingResults(eventId);

        setPollResult(
          pollResponse?.IndividualPollResponses || [],
        );
      }
    } catch (err) {
      console.error("AGM FETCH ERROR:", err);

      if (showLoader) {
        enqueueSnackbar(getErrorMessage(err), {
          variant: "error",
        });
      }
    } finally {
      if (showLoader) {
        setLoader({
          state: false,
        });
      }
    }
  };

  /*
   * ============================================================
   * AGM POLLING
   * ============================================================
   */

  useEffect(() => {
    if (!eventId) {
      return;
    }

    let isMounted = true;

    const runFetch = async (
      showLoader = false,
    ) => {
      if (!isMounted) {
        return;
      }

      await fetchEventInfo({
        showLoader,
      });
    };

    /*
     * FIRST REQUEST
     */

    runFetch(true);

    /*
     * REPEAT EVERY 2 SECONDS
     */

    const intervalId = window.setInterval(() => {
      console.log(
        "INTERVAL TICK:",
        new Date().toLocaleTimeString(),
      );

      runFetch(false);
    }, POLL_INTERVAL_MS);

    return () => {
      console.log("CLEARING AGM INTERVAL");

      isMounted = false;

      window.clearInterval(intervalId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  /*
   * ============================================================
   * MENU ACTIONS
   * ============================================================
   */

  const actions = [
    {
      text: "Home",
      props: {
        onClick: () => navigate("#home"),
      },
    },

    {
      text: "Agenda",
      props: {
        onClick: () => navigate("#agenda"),
      },
      hidden:
        userData?.event?.speakerSection
          ?.speakersRequired &&
        userData?.event?.eventSettings
          ?.userViewInsidePage
          ? false
          : true,
    },

    {
      text: "Speakers",
      props: {
        onClick: () => navigate("#speakers"),
      },
      hidden:
        userData?.event?.speakerSection
          ?.speakersRequired &&
        userData?.event?.eventSettings
          ?.userViewInsidePage
          ? false
          : true,
    },

    {
      text: "Resources",
      props: {
        onClick: () => navigate("#resources"),
      },
      hidden:
        userData?.event?.resourceSection
          ?.resourcesRequired &&
        userData?.event?.eventSettings
          ?.userViewInsidePage
          ? false
          : true,
    },

    {
      text: "Videos",
      props: {
        onClick: () => navigate("#videos"),
      },
      hidden:
        userData?.event?.resourceSection
          ?.resourcesRequired &&
        userData?.event?.eventSettings
          ?.userViewInsidePage
          ? false
          : true,
    },

    {
      text: "Polling",
      props: {
        onClick: () => navigate("#polling"),
      },
      hidden:
        !userData?.livepage?.livePageSettings
          ?.livePoll,
    },

    {
      text:
        userData?.livepage?.livePageSettings
          ?.generalGuidelinesLabel ||
        "General Guidelines Tab",
      props: {
        onClick: () =>
          navigate("#general-guidelines-tab"),
      },
      hidden:
        !userData?.livepage?.livePageSettings
          ?.generalGuidelines,
    },

    {
      text: "FAQs",
      props: {
        onClick: () => navigate("#faqs"),
      },
    },

    {
      text: "Help",
      props: {
        onClick: () => navigate("#help"),
      },
    },

    {
      text: "Feedback",
      props: {
        onClick: () => navigate("#feedback"),
      },
      hidden: !(
        userData?.feedback?.feedbackForm &&
        Object.keys(
          userData.feedback.feedbackForm,
        ).length !== 0
      ),
    },
  ];

  /*
   * ============================================================
   * EVENT SECTION DATA
   * ============================================================
   */

  const {
    resourceSection,
    resourceSetting,
    speakerSection,
    speakerSettings,
  } = userData || {};

  const {
    resourcesRequired,
    resourcesVisibility,
  } = resourceSection || {};

  const {
    speakersRequired,
    speakersVisibility,
  } = speakerSection || {};

  const { galleryView } = userData?.livepage || {};

  const shouldRenderResources =
    resourcesRequired &&
    (resourcesVisibility === "afterLogin" ||
      resourcesVisibility === "bothBeforeAfter");

  const shouldRenderSpeakers =
    speakersRequired &&
    (speakersVisibility === "afterLogin" ||
      speakersVisibility === "bothBeforeAfter");

  const shouldRenderAgmHeader =
    userData?.general?.eventTemplate === "AGM" &&
    galleryView?.showHeader
      ? true
      : userData?.general?.eventTemplate !== "AGM"
        ? true
        : userData?.general?.eventTemplate === "AGM" &&
            !galleryView?.showHeader
          ? false
          : true;

  /*
   * ============================================================
   * SECONDARY CONTENT
   * ============================================================
   */

  const renderSecondaryContent = () => {
    const media =
      userData?.theme?.themeGeneral
        ?.secondaryContentMedia;

    if (!media) {
      return null;
    }

    const cleanMediaUrl = media.split("?")[0];

    const extension = cleanMediaUrl
      .split(".")
      .pop()
      .toLowerCase();

    if (
      [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "svg",
      ].includes(extension)
    ) {
      return (
        <img
          src={media}
          alt="Secondary Content"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      );
    }

    if (extension === "pdf") {
      return (
        <iframe
          src={media}
          title="Secondary Content"
          width="100%"
          height="100%"
          style={{
            border: "none",
          }}
        />
      );
    }

    if (
      ["mp4", "webm", "ogg"].includes(extension)
    ) {
      return (
        <video
          src={media}
          controls
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      );
    }

    return null;
  };

  /*
   * ============================================================
   * LIVE STAGE
   * ============================================================
   */

  const liveStream =
    userData?.livepage?.liveStreamSettings?.find(
      (item) => item?.label === "Live Stream",
    );

  const secondaryContentNode =
    renderSecondaryContent();

  const hasLiveVideo =
    !!liveStream?.liveStreamPlayerUrl;

  const hasSecondaryContent =
    !!secondaryContentNode;

  const isSoloStage =
    (hasLiveVideo && !hasSecondaryContent) ||
    (hasSecondaryContent && !hasLiveVideo);

  const hasNoStageContent =
    !hasLiveVideo && !hasSecondaryContent;

  /*
   * ============================================================
   * TICKER
   * ============================================================
   */

  const tickerPosition =
    userData?.tickers?.tickerSettings
      ?.tickerPosition;

  const showTicker =
    userData?.livepage?.livePageSettings
      ?.liveTickers &&
    userData?.tickers?.tickerSettings
      ?.tickerVisibility === "tickerLive";

  const hasBottomTicker =
    showTicker &&
    tickerPosition === "bottomOfThePage";

  const ticker = showTicker ? (
    <LiveTickers
      messages={[
        userData?.tickers?.tickerSettings
          ?.tickerText,
      ]}
      speed={
        userData?.tickers?.tickerSettings
          ?.tickerSpeed
      }
      direction={
        userData?.tickers?.tickerSettings
          ?.tickerDirection === "rightToLeft"
          ? "right"
          : userData?.tickers?.tickerSettings
                ?.tickerDirection ===
              "leftToRight"
            ? "left"
            : "center"
      }
      position={
        tickerPosition === "middleOfThePage"
          ? "center"
          : tickerPosition === "topOfThePage"
            ? "top"
            : "bottom"
      }
      tickerTextColor={
        userData?.tickers?.tickerSettings
          ?.tickerTextColor
      }
      tickerBgColor={
        userData?.tickers?.tickerSettings
          ?.tickerBgColor
      }
      checkIfTrue={false}
      inline
      viewportEdge={hasBottomTicker}
    />
  ) : null;

  /*
   * ============================================================
   * QUESTION + GUIDELINES BUTTONS
   * ============================================================
   */

  const hasQuestionButtons =
    userData?.livepage?.livePageSettings
      ?.textQuestion ||
    userData?.livepage?.livePageSettings
      ?.videoQuestion;

  const hasGuidelinesButton =
    userData?.theme?.themeGeneral
      ?.guidelinesPopupLabelText &&
    userData?.theme?.themeGeneral
      ?.guidelinesPopupContent;

  const questionButtons = hasQuestionButtons ? (
    <>
      {userData?.livepage?.livePageSettings
        ?.textQuestion ? (
        <PrimaryCTAButton
          ref={buttonRef}
          onClick={() =>
            setModal({
              state: true,
            })
          }
          style={{
            backgroundColor:
              userData?.theme?.themeGeneral
                ?.askQuestionButtonColor,
            color:
              userData?.theme?.themeGeneral
                ?.askQuestionTextColor || "#fff",
            border: "none",
            fontSize: "16px",
          }}
        >
          {userData?.livepage?.livePageSettings
            ?.textQuestionLabel ||
            "Text Question"}
        </PrimaryCTAButton>
      ) : null}
    </>
  ) : null;

  const guidelinesButton =
    hasGuidelinesButton ? (
      <PrimaryCTAButton
        style={{
          backgroundColor:
            userData?.theme?.themeGeneral
              ?.guidelinesPopupButtonBackgroundColor,
          color:
            userData?.theme?.themeGeneral
              ?.guidelinesPopupButtonTextColor ||
            "#fff",
          border: "none",
          fontSize: "16px",
        }}
        onClick={() =>
          setGuidelinesOpen(true)
        }
      >
        {
          userData?.theme?.themeGeneral
            ?.guidelinesPopupLabelText
        }
      </PrimaryCTAButton>
    ) : null;

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <AgmViewportLayout
      image={
        userData?.theme?.themeGeneral
          ?.backgroundImage
      }
      bgColor={
        userData?.theme?.themeGeneral
          ?.bodyBackgroundColor
      }
    >
      {userData?.general?.eventTemplate ===
        "AGM" &&
        userData?.theme?.themeNavigationBar
          ?.navBarTreatment === "alwaysVisible" &&
        shouldRenderAgmHeader && (
          <MenuBurger
            {...{ actions }}
            signOut={false}
            align={true}
            color={
              userData?.theme?.themeNavigationBar
                ?.menuButtonTextColor
            }
            hoverColor={
              userData?.theme?.themeNavigationBar
                ?.menuButtonHoverColor
            }
            menuBackgroundColor={
              userData?.theme?.themeNavigationBar
                ?.menuButtonBgColor
            }
            activeMenuButtonBgColor={
              userData?.theme?.themeNavigationBar
                ?.activeMenuButtonBgColor
            }
            activeMenuButtonTextColor={
              userData?.theme?.themeNavigationBar
                ?.activeMenuButtonTextColor
            }
          />
        )}

      <Header showHeader={false}>
        {userData?.general?.eventTemplate !==
        "AGM" ? (
          userData?.general?.eventTitle ? (
            <WidthMax>
              <DrawerContainer>
                <h2>
                  Agm is not available for this event
                </h2>
              </DrawerContainer>
            </WidthMax>
          ) : null
        ) : (
          <HeroContainer>
            <Center>
              {userData?.general?.eventStatus ===
                "inactive" &&
              (userData?.general?.playbackInfo
                ?.playbackRequired === false ||
                !userData?.general?.playbackInfo
                  ?.playbackRequired) ? (
                <OuterContainer>
                  <DrawerContainer>
                    <h2>Your event is inactive</h2>
                  </DrawerContainer>
                </OuterContainer>
              ) : (
                <OuterContainer>
                  {location.href.includes("agenda") ? (
                    shouldRenderSpeakers && (
                      <AgendaTab
                        speakerSettings={
                          speakerSettings
                        }
                      />
                    )
                  ) : location.href.includes(
                      "speakers",
                    ) ? (
                    shouldRenderSpeakers && (
                      <SpeakersTab
                        speakerSettings={
                          speakerSettings
                        }
                      />
                    )
                  ) : location.href.includes(
                      "resources",
                    ) ? (
                    shouldRenderResources && (
                      <ResourcesTab
                        resourceSetting={
                          resourceSetting
                        }
                      />
                    )
                  ) : location.href.includes(
                      "videos",
                    ) ? (
                    shouldRenderResources && (
                      <VideosTab
                        resourceSetting={
                          resourceSetting
                        }
                      />
                    )
                  ) : location.href.includes(
                      "polling",
                    ) ? (
                    <PollBarChart
                      individualPollResponses={
                        pollResult
                      }
                    />
                  ) : location.href.includes(
                      "feedback",
                    ) ? (
                    <Feedback
                      fields={fields}
                      initialState={initialState}
                      userData={userData}
                    />
                  ) : location.href.includes(
                      "general-guidelines-tab",
                    ) ? (
                    <GeneralGuidelinesTab
                      generalGuidelinesTab={
                        userData?.additionaltabs
                          ?.generalGuidelinesTab
                      }
                    />
                  ) : location.href
                      .toLowerCase()
                      .includes("faqs") ? (
                    <FAQs
                      data={
                        userData?.faq?.faq ||
                        userData?.faq ||
                        []
                      }
                    />
                  ) : location.href
                      .toLowerCase()
                      .includes("help") ? (
                    <FAQs
                      data={
                        userData?.help?.help ||
                        userData?.help ||
                        []
                      }
                      help={true}
                    />
                  ) : userData?.general
                      ?.eventStatus === "inactive" &&
                    userData?.general?.playbackInfo
                      ?.playbackRequired &&
                    !userData?.general?.playbackInfo
                      ?.playbackVideo?.length ? (
                    <DrawerContainer>
                      <h2>
                        Playback is getting ready for
                        this event
                      </h2>
                    </DrawerContainer>
                  ) : userData?.general
                      ?.eventStatus === "inactive" &&
                    userData?.general?.playbackInfo
                      ?.playbackRequired &&
                    userData?.general?.playbackInfo
                      ?.playbackVideo?.length ? (
                    <VideoWrapper>
                      <VideoPlayer
                        src={
                          userData?.general
                            ?.playbackInfo
                            ?.playbackVideo
                        }
                      />
                    </VideoWrapper>
                  ) : (
                    <Container
                      hasBottomTicker={
                        hasBottomTicker
                      }
                    >
                      {userData?.tickers
                        ?.tickerSettings
                        ?.tickerVisibility ===
                        "pauseTheTicker" &&
                      userData?.livepage
                        ?.livePageSettings
                        ?.liveTickers ? (
                        <TopTickerPauseContainer
                          style={{
                            color:
                              userData?.tickers
                                ?.tickerSettings
                                ?.tickerTextColor,
                            backgroundColor:
                              userData?.tickers
                                ?.tickerSettings
                                ?.tickerBgColor,
                          }}
                        >
                          {
                            userData?.tickers
                              ?.tickerSettings
                              ?.tickerText
                          }
                        </TopTickerPauseContainer>
                      ) : (
                        tickerPosition ===
                          "topOfThePage" && ticker
                      )}

                      {userData?.livepage
                        ?.livePageSettings
                        ?.contentFormat ===
                        "video" &&
                        !hasNoStageContent && (
                          <StageRow
                            key={
                              liveStream?.streamingKey ||
                              "stage-row"
                            }
                            reverse={
                              userData?.theme
                                ?.themeGeneral
                                ?.secondaryContentLayout ===
                              "secondaryContentLeftLiveVideoRight"
                            }
                            solo={isSoloStage}
                          >
                            {hasLiveVideo && (
                              <StageColumn
                                solo={
                                  hasLiveVideo &&
                                  !hasSecondaryContent
                                }
                              >
                                <VideoPane>
                                  <VideoBoxWrapper>
                                    <VideoBox
                                      src={
                                        liveStream.liveStreamPlayerUrl
                                      }
                                    />
                                  </VideoBoxWrapper>
                                </VideoPane>

                                {hasQuestionButtons && (
                                  <ColumnActionBar>
                                    {questionButtons}
                                  </ColumnActionBar>
                                )}
                              </StageColumn>
                            )}

                            {hasSecondaryContent && (
                              <StageColumn
                                solo={
                                  hasSecondaryContent &&
                                  !hasLiveVideo
                                }
                              >
                                {/* <SecondaryContentCard>
                                  {
                                    secondaryContentNode
                                  }
                                </SecondaryContentCard> */}
                                <SecondaryContentCard tabIndex={0}>
                                  {secondaryContentNode}

                                  <SecondaryDownloadLink
                                    href={userData?.theme?.themeGeneral?.secondaryContentMedia}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Download secondary content"
                                    aria-label="Download secondary content"
                                  >
                                    <GetAppIcon />
                                  </SecondaryDownloadLink>
                                </SecondaryContentCard>

                                {hasGuidelinesButton && (
                                  <ColumnActionBar>
                                    {guidelinesButton}
                                  </ColumnActionBar>
                                )}
                              </StageColumn>
                            )}
                          </StageRow>
                        )}

                      {tickerPosition ===
                        "middleOfThePage" && ticker}

                      {hasNoStageContent &&
                        (hasQuestionButtons ||
                          hasGuidelinesButton) && (
                          <ActionBar solo>
                            {hasQuestionButtons && (
                              <ActionBarGroup>
                                {questionButtons}
                              </ActionBarGroup>
                            )}

                            {hasGuidelinesButton && (
                              <ActionBarGroup>
                                {guidelinesButton}
                              </ActionBarGroup>
                            )}
                          </ActionBar>
                        )}

                      {tickerPosition ===
                        "bottomOfThePage" && ticker}

                      {guidelinesOpen && (
                        <GuidelinesDialog
                          open={guidelinesOpen}
                          onClose={() =>
                            setGuidelinesOpen(false)
                          }
                          title={
                            userData?.theme
                              ?.themeGeneral
                              ?.guidelinesPopupLabelText
                          }
                          content={
                            userData?.theme
                              ?.themeGeneral
                              ?.guidelinesPopupContent
                          }
                          buttonText="Got it, Thanks"
                          buttonBackgroundColor={
                            userData?.theme
                              ?.themeGeneral
                              ?.guidelinesPopupButtonBackgroundColor
                          }
                          buttonTextColor={
                            userData?.theme
                              ?.themeGeneral
                              ?.guidelinesPopupButtonTextColor
                          }
                        />
                      )}

                      {modal?.state && (
                        <ChatModal
                          modal={modal}
                          setModal={setModal}
                          eventId={eventId}
                          communicateFields={
                            userData?.communicate
                              ?.communicateFields
                          }
                          askQuestionButtonColor={
                            userData?.theme
                              ?.themeGeneral
                              ?.askQuestionButtonColor
                          }
                          askQuestionTextColor={
                            userData?.theme
                              ?.themeGeneral
                              ?.askQuestionTextColor ||
                            "#fff"
                          }
                        />
                      )}
                    </Container>
                  )}
                </OuterContainer>
              )}
            </Center>
          </HeroContainer>
        )}
      </Header>
    </AgmViewportLayout>
  );
};

export default AgmLive;

/*
 * ============================================================
 * STYLED COMPONENTS
 * ============================================================
 */


const AgmViewportLayout = styled(Layout)`
  position: relative;

  width: 100%;
  height: 100%;

  min-height: 0;

  overflow: hidden;

  @media (max-width: 900px) and (orientation: landscape) {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }
`;

export const OuterContainer = styled.div`
  width: 100%;
  height: 100%;

  min-height: 0;

  display: flex;
  justify-content: center;
  align-items: stretch;

  box-sizing: border-box;
  @media (max-width: 900px) and (orientation: landscape) {
  height: auto;
  min-height: 100%;
}
`;

export const Container = styled.div`
  /*
   * IMPORTANT:
   *
   * Container is attached directly to the viewport.
   *
   * It starts exactly below StyledAppBar and ends at
   * the bottom of the viewport.
   *
   * This is the ONLY scroll owner for the AGM live stage.
   */

  position: fixed;

  top: var(--agm-header-height, 125px);
  left: 0;
  right: 0;
  bottom: 0;

  width: 100%;

  min-height: 0;

  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 0;
  margin: 0 auto;

  /*
   * Bottom ticker is position: fixed.
   *
   * Therefore it does not contribute to Container's
   * scrollHeight.
   *
   * Reserve enough bottom space so the final button/content
   * can scroll completely above the ticker.
   */

  padding:
    clamp(12px, 2vh, 24px)
    clamp(12px, 2vw, 28px)
    ${({ hasBottomTicker }) =>
      hasBottomTicker
        ? "calc(clamp(58px, 8vh, 72px) + 24px)"
        : "clamp(12px, 2vh, 24px)"};

  box-sizing: border-box;

  overflow-x: hidden;
  overflow-y: auto;

  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;

  @media (max-width: 900px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media (max-width: 600px) {
    padding-left: 12px;
    padding-right: 12px;
  }

  @media (max-width: 900px) and (orientation: landscape) {
    position: relative;

    top: auto;
    left: auto;
    right: auto;
    bottom: auto;

    overflow: visible;

    min-height: auto;
  }
`;

const StageRow = styled.div`
  width: 100%;

  /*
   * StageRow must grow naturally according to the complete
   * height of StageColumn children and their action buttons.
   */

  flex: 0 0 auto;
  min-height: 0;

  margin: 0 auto;

  display: flex;

  flex-direction: ${({ reverse }) =>
    reverse ? "row-reverse" : "row"};

  align-items: flex-start;
  justify-content: center;

  gap: clamp(12px, 2vw, 28px);

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const StageColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  flex: ${({ solo }) =>
    solo ? "0 1 900px" : "1 1 0"};

  width: ${({ solo }) =>
    solo ? "100%" : "auto"};

  max-width: ${({ solo }) =>
    solo ? "900px" : "none"};

  min-width: 0;

  margin: ${({ solo }) =>
    solo ? "0 auto" : "0"};

  /*
   * Mobile/tablet:
   *
   * StageColumn uses its natural content height.
   * Video/secondary pane + action button are all included
   * in Container.scrollHeight.
   */

  @media (max-width: 900px) {
    flex: 0 0 auto;
    width: 100%;
    max-width: 100%;
  }
`;

const StagePaneBase = styled.div`
  position: relative;

  width: 100%;
  aspect-ratio: 16 / 9;

  max-height: min(62vh, 720px);

  display: flex;
  overflow: hidden;

  @media (max-width: 900px) {
    max-height: none;
  }

  @media (max-width: 600px) {
    border-radius: 4px;
  }
`;

const VideoPane = styled(StagePaneBase)`
  background: #000;
`;

const SecondaryDownloadLink = styled.a`
  position: absolute;

  top: 10px;
  right: 10px;

  z-index: 10;

  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: rgba(0, 0, 0, 0.65);
  color: #fff;

  text-decoration: none;
  cursor: pointer;

  opacity: 0;
  visibility: hidden;

  transform: translateY(-4px);

  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    visibility 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  svg {
    font-size: 22px;
  }

  @media (hover: none), (pointer: coarse) {
    width: 32px;
    height: 32px;

    top: 8px;
    right: 8px;

    svg {
      font-size: 19px;
    }
  }
`;

const SecondaryContentCard = styled(StagePaneBase)`
  justify-content: center;
  align-items: center;

  background: #fff;

  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);

  iframe,
  img,
  video {
    width: 100%;
    height: 100%;

    border: 0;
    object-fit: contain;
  }

  /* Desktop/laptop */
  @media (hover: hover) and (pointer: fine) {
    &:hover ${SecondaryDownloadLink} {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }

  /* Touch devices */
  @media (hover: none), (pointer: coarse) {
    &:focus-within ${SecondaryDownloadLink} {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }
`;

const VideoWrapper = styled.div`
  width: 70%;
  margin: 2% 14%;
  padding: 4%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  @media (max-width: 900px) {
    width: 85%;
    margin: 3% 7.5%;
  }

  @media (max-width: 600px) {
    width: 92%;
    margin: 4% 4%;
    padding: 3%;
  }
`;

export const AboutUsBody = styled.section`
  background-color: #f9f9f9;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 600px) {
    height: unset;
  }
`;

export const GreySection = styled.section`
  display: flex;
  flex-direction: column;

  padding: 60px 0;

  align-items: center;

  background-color: #f9f9f9;

  width: 100vw;

  @media (max-width: 600px) {
    padding: 32px 0;
  }
`;

export const WhiteSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100vw;
`;

export const H2 = styled.h2`
  font-family: ${theme.fonts.primaryFontBold};

  color: ${COLORS.COLOR_DARK};

  font-size: 22px;

  margin-bottom: 32px;

  cursor: default;

  padding: 0 20px;

  margin: 0 auto;

  @media (max-width: 1024px) {
    font-size: 24px;
    text-align: center;
  }

  @media (max-width: 600px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

export const BodyWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  align-items: center;
  justify-content: center;

  font-size: 18px;

  padding: 1%;

  color: ${COLORS.BTN_GREEN};

  font-family: ${theme.fonts.primaryFontBold};

  width: 100%;

  @media (max-width: 700px) {
    flex-direction: column;
    font-size: 15px;
  }
`;

export const Loader = styled.div`
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  width: 100%;
  height: 100%;

  min-height: 0;

  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  box-sizing: border-box;
  @media (max-width: 900px) and (orientation: landscape) {
  height: auto;
  min-height: 100%;
}
`;

const ColumnActionBar = styled.div`
  display: flex;

  gap: 10px;

  margin-top: 12px;

  width: auto;

  /*
   * Important for scroll layout:
   *
   * Do not absolutely/fixed-position these buttons.
   * They stay in normal flow below their corresponding pane.
   */

  flex: 0 0 auto;

  & > button {
    flex: 1 1 0;

    min-width: 0;

    height: 44px;

    padding: 0 18px;
  }

  @media (max-width: 600px) {
    & > button {
      height: 40px;

      padding: 0 14px;

      font-size: 13px;
    }
  }
`;

const ActionBar = styled.div`
  width: 100%;

  max-width: ${({ solo }) =>
    solo ? "900px" : "100%"};

  margin: 20px auto 0;

  display: flex;
  flex-wrap: wrap;

  align-items: stretch;

  gap: 12px;

  flex: 0 0 auto;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ActionBarGroup = styled.div`
  display: flex;

  flex: 1 1 0;

  gap: 10px;

  & > button {
    flex: 1 1 0;

    min-width: 0;

    height: 46px;

    padding: 0 18px;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

export const TopTickerPauseContainer = styled.div`
  position: relative;

  max-width: 100%;

  flex: 0 0 auto;

  font-size: 18px;

  margin-bottom:
    clamp(12px, 2vh, 20px);

  padding: 8px 1px;

  box-sizing: border-box;

  opacity: 0.9;

  text-align: center;

  overflow: hidden;

  pointer-events: none;

  z-index: 1;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const VideoBoxWrapper = styled.div`
  width: 100%;
  height: 100%;

  & > * {
    width: 100% !important;
    height: 100% !important;
  }

  video,
  iframe {
    width: 100% !important;
    height: 100% !important;

    object-fit: contain;
  }
`;

const WidthMax = styled.div`
  width: 100vw;
  min-height: 100vh;
`;
